import { isDemoMode, db } from '../firebase';
import type { Qualification, SkillTreeDef } from '../types';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';

// ── Demo helpers ──

const DEMO_QUALS_KEY = 'demo_qualifications';
const DEMO_TREES_KEY = 'demo_skill_trees';

const getDemoQualifications = (): Qualification[] => {
  const stored = localStorage.getItem(DEMO_QUALS_KEY);
  if (!stored) return [];
  try {
    return JSON.parse(stored).map((q: any) => ({
      ...q,
      createdAt: q.createdAt ? new Date(q.createdAt) : undefined,
      updatedAt: q.updatedAt ? new Date(q.updatedAt) : undefined,
    }));
  } catch {
    return [];
  }
};

const saveDemoQualifications = (quals: Qualification[]) => {
  localStorage.setItem(DEMO_QUALS_KEY, JSON.stringify(quals));
};

const getDemoSkillTrees = (): SkillTreeDef[] => {
  const stored = localStorage.getItem(DEMO_TREES_KEY);
  if (!stored) return [];
  try {
    return JSON.parse(stored).map((t: any) => ({
      ...t,
      createdAt: t.createdAt ? new Date(t.createdAt) : undefined,
      updatedAt: t.updatedAt ? new Date(t.updatedAt) : undefined,
    }));
  } catch {
    return [];
  }
};

const saveDemoSkillTrees = (trees: SkillTreeDef[]) => {
  localStorage.setItem(DEMO_TREES_KEY, JSON.stringify(trees));
};

// ── Firestore conversion ──

const firestoreToQualification = (id: string, data: any): Qualification => ({
  id,
  name: data.name || '',
  shortName: data.shortName || '',
  description: data.description || '',
  prerequisites: data.prerequisites || [],
  optional: data.optional ?? false,
  order: data.order ?? 0,
  items: (data.items || []).map((item: any, idx: number) => ({
    id: item.id,
    category: item.category,
    title: item.title,
    description: item.description || '',
    hasRubric: item.hasRubric ?? false,
    order: item.order ?? idx,
  })),
  version: data.version ?? 1,
  createdAt: data.createdAt?.toDate?.() || undefined,
  updatedAt: data.updatedAt?.toDate?.() || undefined,
  updatedBy: data.updatedBy || undefined,
  deletedAt: data.deletedAt?.toDate?.() || null,
  deletedBy: data.deletedBy || null,
});

const qualificationToFirestore = (qual: Qualification) => ({
  name: qual.name,
  shortName: qual.shortName,
  description: qual.description,
  prerequisites: qual.prerequisites,
  optional: qual.optional,
  order: qual.order,
  items: qual.items.map((item, idx) => ({
    id: item.id,
    category: item.category,
    title: item.title,
    description: item.description,
    hasRubric: item.hasRubric,
    order: item.order ?? idx,
  })),
  version: qual.version ?? 1,
  updatedAt: Timestamp.now(),
  updatedBy: qual.updatedBy || '',
});

const firestoreToSkillTree = (id: string, data: any): SkillTreeDef => ({
  id,
  name: data.name || '',
  shortName: data.shortName || '',
  description: data.description || '',
  qualificationIds: data.qualificationIds || [],
  defaultDeadlines: data.defaultDeadlines || {},
  version: data.version ?? 1,
  createdAt: data.createdAt?.toDate?.() || undefined,
  updatedAt: data.updatedAt?.toDate?.() || undefined,
  updatedBy: data.updatedBy || undefined,
  deletedAt: data.deletedAt?.toDate?.() || null,
  deletedBy: data.deletedBy || null,
});

const skillTreeToFirestore = (tree: SkillTreeDef) => ({
  name: tree.name,
  shortName: tree.shortName,
  description: tree.description,
  qualificationIds: tree.qualificationIds,
  defaultDeadlines: tree.defaultDeadlines,
  version: tree.version ?? 1,
  updatedAt: Timestamp.now(),
  updatedBy: tree.updatedBy || '',
});

// ── In-memory cache ──

const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
}

let qualsCache: CacheEntry<Qualification[]> | null = null;
let treesCache: CacheEntry<SkillTreeDef[]> | null = null;

const isCacheValid = <T>(entry: CacheEntry<T> | null): entry is CacheEntry<T> =>
  entry !== null && Date.now() - entry.fetchedAt < CACHE_TTL_MS;

// ── Public API ──

class DefinitionService {
  // ── Qualifications ──

  async getQualifications(includeDeleted = false): Promise<Qualification[]> {
    if (isDemoMode) {
      const all = getDemoQualifications();
      return includeDeleted ? all : all.filter((q) => !q.deletedAt);
    }

    if (isCacheValid(qualsCache)) {
      return includeDeleted ? qualsCache.data : qualsCache.data.filter((q) => !q.deletedAt);
    }

    if (!db) return [];
    const snapshot = await getDocs(collection(db, 'qualifications'));
    const quals = snapshot.docs
      .map((d) => firestoreToQualification(d.id, d.data()))
      .sort((a, b) => a.order - b.order);

    qualsCache = { data: quals, fetchedAt: Date.now() };
    return includeDeleted ? quals : quals.filter((q) => !q.deletedAt);
  }

  async getQualification(qualId: string): Promise<Qualification | null> {
    if (isDemoMode) {
      return getDemoQualifications().find((q) => q.id === qualId) || null;
    }

    // Check cache first
    if (isCacheValid(qualsCache)) {
      return qualsCache.data.find((q) => q.id === qualId) || null;
    }

    if (!db) return null;
    const snap = await getDoc(doc(db, 'qualifications', qualId));
    if (!snap.exists()) return null;
    return firestoreToQualification(snap.id, snap.data());
  }

  async saveQualification(qual: Qualification, updatedBy: string): Promise<Qualification> {
    const now = new Date();
    const isNew = !qual.version || qual.version === 0;
    const saved: Qualification = {
      ...qual,
      version: (qual.version || 0) + 1,
      updatedAt: now,
      updatedBy,
      createdAt: isNew ? now : qual.createdAt,
    };

    if (isDemoMode) {
      const all = getDemoQualifications();
      const idx = all.findIndex((q) => q.id === saved.id);
      if (idx >= 0) all[idx] = saved;
      else all.push(saved);
      saveDemoQualifications(all);
      this.invalidateCache();
      return saved;
    }

    if (!db) throw new Error('Firebase not configured');
    const data = qualificationToFirestore(saved);
    if (isNew) {
      (data as any).createdAt = Timestamp.now();
    }
    await setDoc(doc(db, 'qualifications', saved.id), data, { merge: true });
    this.invalidateCache();
    return saved;
  }

  async deleteQualification(qualId: string, deletedBy: string): Promise<void> {
    if (isDemoMode) {
      const all = getDemoQualifications();
      const idx = all.findIndex((q) => q.id === qualId);
      if (idx >= 0) {
        all[idx].deletedAt = new Date();
        all[idx].deletedBy = deletedBy;
        saveDemoQualifications(all);
      }
      this.invalidateCache();
      return;
    }

    if (!db) throw new Error('Firebase not configured');
    await updateDoc(doc(db, 'qualifications', qualId), {
      deletedAt: Timestamp.now(),
      deletedBy,
      updatedAt: Timestamp.now(),
    });
    this.invalidateCache();
  }

  async restoreQualification(qualId: string): Promise<void> {
    if (isDemoMode) {
      const all = getDemoQualifications();
      const idx = all.findIndex((q) => q.id === qualId);
      if (idx >= 0) {
        all[idx].deletedAt = null;
        all[idx].deletedBy = null;
        saveDemoQualifications(all);
      }
      this.invalidateCache();
      return;
    }

    if (!db) throw new Error('Firebase not configured');
    await updateDoc(doc(db, 'qualifications', qualId), {
      deletedAt: null,
      deletedBy: null,
      updatedAt: Timestamp.now(),
    });
    this.invalidateCache();
  }

  // ── Skill Trees ──

  async getSkillTrees(includeDeleted = false): Promise<SkillTreeDef[]> {
    if (isDemoMode) {
      const all = getDemoSkillTrees();
      return includeDeleted ? all : all.filter((t) => !t.deletedAt);
    }

    if (isCacheValid(treesCache)) {
      return includeDeleted ? treesCache.data : treesCache.data.filter((t) => !t.deletedAt);
    }

    if (!db) return [];
    const snapshot = await getDocs(collection(db, 'skillTrees'));
    const trees = snapshot.docs.map((d) => firestoreToSkillTree(d.id, d.data()));

    treesCache = { data: trees, fetchedAt: Date.now() };
    return includeDeleted ? trees : trees.filter((t) => !t.deletedAt);
  }

  async getSkillTree(treeId: string): Promise<SkillTreeDef | null> {
    if (isDemoMode) {
      return getDemoSkillTrees().find((t) => t.id === treeId) || null;
    }

    if (isCacheValid(treesCache)) {
      return treesCache.data.find((t) => t.id === treeId) || null;
    }

    if (!db) return null;
    const snap = await getDoc(doc(db, 'skillTrees', treeId));
    if (!snap.exists()) return null;
    return firestoreToSkillTree(snap.id, snap.data());
  }

  async saveSkillTree(tree: SkillTreeDef, updatedBy: string): Promise<SkillTreeDef> {
    const now = new Date();
    const isNew = !tree.version || tree.version === 0;
    const saved: SkillTreeDef = {
      ...tree,
      version: (tree.version || 0) + 1,
      updatedAt: now,
      updatedBy,
      createdAt: isNew ? now : tree.createdAt,
    };

    if (isDemoMode) {
      const all = getDemoSkillTrees();
      const idx = all.findIndex((t) => t.id === saved.id);
      if (idx >= 0) all[idx] = saved;
      else all.push(saved);
      saveDemoSkillTrees(all);
      this.invalidateCache();
      return saved;
    }

    if (!db) throw new Error('Firebase not configured');
    const data = skillTreeToFirestore(saved);
    if (isNew) {
      (data as any).createdAt = Timestamp.now();
    }
    await setDoc(doc(db, 'skillTrees', saved.id), data, { merge: true });
    this.invalidateCache();
    return saved;
  }

  async deleteSkillTree(treeId: string, deletedBy: string): Promise<void> {
    if (isDemoMode) {
      const all = getDemoSkillTrees();
      const idx = all.findIndex((t) => t.id === treeId);
      if (idx >= 0) {
        all[idx].deletedAt = new Date();
        all[idx].deletedBy = deletedBy;
        saveDemoSkillTrees(all);
      }
      this.invalidateCache();
      return;
    }

    if (!db) throw new Error('Firebase not configured');
    await updateDoc(doc(db, 'skillTrees', treeId), {
      deletedAt: Timestamp.now(),
      deletedBy,
      updatedAt: Timestamp.now(),
    });
    this.invalidateCache();
  }

  async restoreSkillTree(treeId: string): Promise<void> {
    if (isDemoMode) {
      const all = getDemoSkillTrees();
      const idx = all.findIndex((t) => t.id === treeId);
      if (idx >= 0) {
        all[idx].deletedAt = null;
        all[idx].deletedBy = null;
        saveDemoSkillTrees(all);
      }
      this.invalidateCache();
      return;
    }

    if (!db) throw new Error('Firebase not configured');
    await updateDoc(doc(db, 'skillTrees', treeId), {
      deletedAt: null,
      deletedBy: null,
      updatedAt: Timestamp.now(),
    });
    this.invalidateCache();
  }

  // ── Import/Export ──

  async exportTree(treeId: string, exportedBy: string): Promise<object> {
    const tree = await this.getSkillTree(treeId);
    if (!tree) throw new Error(`Tree ${treeId} not found`);

    const allQuals = await this.getQualifications();
    const treeQuals = tree.qualificationIds
      .map((qid) => allQuals.find((q) => q.id === qid))
      .filter(Boolean) as Qualification[];

    return {
      formatVersion: '1',
      exportedAt: new Date().toISOString(),
      exportedBy,
      tree: {
        id: tree.id,
        name: tree.name,
        shortName: tree.shortName,
        description: tree.description,
        qualificationIds: tree.qualificationIds,
        defaultDeadlines: tree.defaultDeadlines,
        version: tree.version,
      },
      qualifications: treeQuals.map((q) => ({
        id: q.id,
        name: q.name,
        shortName: q.shortName,
        description: q.description,
        prerequisites: q.prerequisites,
        optional: q.optional,
        order: q.order,
        items: q.items,
        version: q.version,
      })),
      metadata: {
        totalQuals: treeQuals.length,
        totalItems: treeQuals.reduce((sum, q) => sum + q.items.length, 0),
      },
    };
  }

  async importTree(
    json: any,
    mode: 'create' | 'update',
    updatedBy: string
  ): Promise<{ treeId: string; qualsImported: number }> {
    if (!json?.tree || !json?.qualifications) {
      throw new Error('Invalid import format: missing tree or qualifications');
    }

    const tree: SkillTreeDef = {
      id: json.tree.id,
      name: json.tree.name,
      shortName: json.tree.shortName,
      description: json.tree.description,
      qualificationIds: json.tree.qualificationIds,
      defaultDeadlines: json.tree.defaultDeadlines || {},
    };

    const quals: Qualification[] = json.qualifications.map((q: any) => ({
      id: q.id,
      name: q.name,
      shortName: q.shortName,
      description: q.description,
      prerequisites: q.prerequisites || [],
      optional: q.optional ?? false,
      order: q.order ?? 0,
      items: (q.items || []).map((item: any, idx: number) => ({
        id: item.id,
        category: item.category,
        title: item.title,
        description: item.description || '',
        hasRubric: item.hasRubric ?? false,
        order: item.order ?? idx,
      })),
    }));

    if (mode === 'create') {
      // Check for ID conflict
      const existing = await this.getSkillTree(tree.id);
      if (existing) {
        throw new Error(`Tree with ID "${tree.id}" already exists. Use update mode or change the ID.`);
      }
    }

    // Save all qualifications then the tree
    for (const qual of quals) {
      await this.saveQualification(qual, updatedBy);
    }
    await this.saveSkillTree(tree, updatedBy);

    return { treeId: tree.id, qualsImported: quals.length };
  }

  // ── Seeding ──

  async seedFromStatic(
    qualifications: Qualification[],
    trees: SkillTreeDef[],
    updatedBy: string
  ): Promise<{ qualsSeeded: number; treesSeeded: number }> {
    let qualsSeeded = 0;
    let treesSeeded = 0;

    for (const qual of qualifications) {
      const existing = await this.getQualification(qual.id);
      if (existing && (existing.version || 0) >= (qual.version || 1)) continue;
      await this.saveQualification({ ...qual, version: 0 }, updatedBy);
      qualsSeeded++;
    }

    for (const tree of trees) {
      const existing = await this.getSkillTree(tree.id);
      if (existing && (existing.version || 0) >= (tree.version || 1)) continue;
      await this.saveSkillTree({ ...tree, version: 0 }, updatedBy);
      treesSeeded++;
    }

    this.invalidateCache();
    return { qualsSeeded, treesSeeded };
  }

  // ── Cache management ──

  invalidateCache(): void {
    qualsCache = null;
    treesCache = null;
  }
}

export const definitionService = new DefinitionService();
